/**
 * My Library Service - React Query Hooks
 */

import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  getGuestImages,
  getUserImages,
  getUserGuestUploads,
  uploadGuestImages,
  uploadUserImages,
  cleanupGuestImages,
  deleteGuestImages,
  deleteUserImages,
  toggleFavoriteImages,
  transferGuestImages,
  type LibraryResponse,
} from './api'
import {
  DEFAULT_PAGE_LIMIT,
  DEFAULT_STALE_TIME,
  DEFAULT_GC_TIME,
  SHORT_STALE_TIME,
} from '@/services/constants'

// ==================== Query Keys ====================

export const libraryKeys = {
  all: ['library'] as const,
  guest: (invitationId: string) =>
    [...libraryKeys.all, 'guest', invitationId] as const,
  user: () => [...libraryKeys.all, 'user'] as const,
  userFavorites: () => [...libraryKeys.all, 'user', 'favorites'] as const,
  userGuestUploads: () =>
    [...libraryKeys.all, 'user', 'guest-uploads'] as const,
}

// ==================== Query Hooks ====================

/**
 * Get guest images for an invitation (single page)
 */
export function useGuestImages(
  invitationId: string | undefined,
  options?: { limit?: number; offset?: number; enabled?: boolean }
) {
  return useQuery({
    queryKey: [
      ...libraryKeys.guest(invitationId ?? ''),
      'page',
      options?.offset ?? 0,
    ],
    queryFn: () => getGuestImages(invitationId!, options),
    enabled: !!invitationId && options?.enabled !== false,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    refetchOnWindowFocus: false, // Don't refetch on tab focus (data rarely changes externally)
  })
}

/**
 * Get guest images with infinite scroll / lazy loading
 */
export function useGuestImagesInfinite(
  invitationId: string | undefined,
  options?: { limit?: number; enabled?: boolean }
) {
  const limit = options?.limit ?? DEFAULT_PAGE_LIMIT

  return useInfiniteQuery({
    queryKey: libraryKeys.guest(invitationId ?? ''),
    queryFn: ({ pageParam = 0 }) =>
      getGuestImages(invitationId!, { limit, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: LibraryResponse) =>
      lastPage.data.hasMore
        ? lastPage.data.offset + lastPage.data.limit
        : undefined,
    enabled: !!invitationId && options?.enabled !== false,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    refetchOnWindowFocus: false,
  })
}

/**
 * Get user images (flat library - images belong to user, not invitation)
 * @param options.favorites - If true, only return favorited images
 */
export function useUserImages(options?: {
  limit?: number
  offset?: number
  favorites?: boolean
  enabled?: boolean
}) {
  const queryKey = options?.favorites
    ? [...libraryKeys.userFavorites(), 'page', options?.offset ?? 0]
    : [...libraryKeys.user(), 'page', options?.offset ?? 0]

  return useQuery({
    queryKey,
    queryFn: () => getUserImages(options),
    enabled: options?.enabled !== false,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    refetchOnWindowFocus: false,
  })
}

/**
 * Get user images with infinite scroll / lazy loading
 * @param options.favorites - If true, only return favorited images
 */
export function useUserImagesInfinite(options?: {
  limit?: number
  favorites?: boolean
  enabled?: boolean
}) {
  const limit = options?.limit ?? DEFAULT_PAGE_LIMIT
  const queryKey = options?.favorites
    ? libraryKeys.userFavorites()
    : libraryKeys.user()

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 0 }) =>
      getUserImages({
        limit,
        offset: pageParam,
        favorites: options?.favorites,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: LibraryResponse) =>
      lastPage.data.hasMore
        ? lastPage.data.offset + lastPage.data.limit
        : undefined,
    enabled: options?.enabled !== false,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    refetchOnWindowFocus: false,
  })
}

/**
 * Get guest uploads from user's invitations (single page)
 * These are photos uploaded by guests (not the user)
 * Note: Backend does NOT cache this endpoint - always fresh from DB
 * @param options.invitationId - Filter to specific invitation (optional)
 */
export function useUserGuestUploads(options?: {
  invitationId?: string
  limit?: number
  offset?: number
  enabled?: boolean
}) {
  // Include invitationId in query key for proper caching
  const queryKey = options?.invitationId
    ? [
        ...libraryKeys.userGuestUploads(),
        options.invitationId,
        'page',
        options?.offset ?? 0,
      ]
    : [...libraryKeys.userGuestUploads(), 'all', 'page', options?.offset ?? 0]

  return useQuery({
    queryKey,
    queryFn: () => getUserGuestUploads(options),
    enabled: options?.enabled !== false,
    // Backend doesn't cache this - use shorter stale time for fresher data
    staleTime: SHORT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    refetchOnWindowFocus: true, // Refetch on focus since data may have changed
  })
}

/**
 * Get guest uploads with infinite scroll / lazy loading
 * Note: Backend does NOT cache this endpoint - always fresh from DB
 * @param options.invitationId - Filter to specific invitation (optional)
 */
export function useUserGuestUploadsInfinite(options?: {
  invitationId?: string
  limit?: number
  enabled?: boolean
}) {
  const limit = options?.limit ?? DEFAULT_PAGE_LIMIT
  const queryKey = options?.invitationId
    ? [...libraryKeys.userGuestUploads(), options.invitationId, 'infinite']
    : [...libraryKeys.userGuestUploads(), 'all', 'infinite']

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 0 }) =>
      getUserGuestUploads({
        invitationId: options?.invitationId,
        limit,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: LibraryResponse) =>
      lastPage.data.hasMore
        ? lastPage.data.offset + lastPage.data.limit
        : undefined,
    enabled: options?.enabled !== false,
    // Backend doesn't cache this - use shorter stale time for fresher data
    staleTime: SHORT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    refetchOnWindowFocus: true,
  })
}

// ==================== Mutation Hooks ====================

/**
 * Upload images (guest)
 */
export function useUploadGuestImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      files,
      invitationId,
      type = 'og',
    }: {
      files: File | File[]
      invitationId: string
      type?: 'og' | 'edit'
    }) => uploadGuestImages(files, invitationId, type),
    onSuccess: (_, variables) => {
      // Invalidate guest images query
      queryClient.invalidateQueries({
        queryKey: libraryKeys.guest(variables.invitationId),
      })
    },
  })
}

/**
 * Upload images (authenticated user)
 * Images belong to user's flat library
 */
export function useUploadUserImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      files,
      type = 'og',
    }: {
      files: File | File[]
      type?: 'og' | 'edit'
    }) => uploadUserImages(files, type),
    onSuccess: () => {
      // Invalidate all user images queries (both with and without guest uploads)
      queryClient.invalidateQueries({
        queryKey: libraryKeys.user(),
      })
    },
  })
}

/**
 * Cleanup all guest images (abandoned session)
 * Used when user navigates away without signing in
 */
export function useCleanupGuestImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invitationId: string) => cleanupGuestImages(invitationId),
    onSuccess: (_, invitationId) => {
      // Remove the query from cache entirely
      queryClient.removeQueries({
        queryKey: libraryKeys.guest(invitationId),
      })
    },
  })
}

/**
 * Delete specific guest images by IDs
 */
export function useDeleteGuestImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      invitationId,
      imageIds,
    }: {
      invitationId: string
      imageIds: string[]
    }) => deleteGuestImages(invitationId, imageIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: libraryKeys.guest(variables.invitationId),
      })
    },
  })
}

/**
 * Delete images (authenticated user)
 */
export function useDeleteUserImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ imageIds }: { imageIds: string[] }) =>
      deleteUserImages(imageIds),
    onSuccess: () => {
      // Invalidate all user images queries (including both guest upload variants)
      queryClient.invalidateQueries({
        queryKey: libraryKeys.user(),
      })
    },
  })
}

/**
 * Toggle favorite status for images
 */
export function useToggleFavoriteImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      imageIds,
      favorite,
    }: {
      imageIds: string[]
      favorite: boolean
    }) => toggleFavoriteImages(imageIds, favorite),
    onSuccess: () => {
      // Invalidate all user images queries
      queryClient.invalidateQueries({
        queryKey: libraryKeys.user(),
      })
    },
  })
}

/**
 * Transfer guest images to user account after sign-in
 */
export function useTransferGuestImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invitationId: string) => transferGuestImages(invitationId),
    onSuccess: (_, invitationId) => {
      // Remove guest query from cache (images no longer there)
      queryClient.removeQueries({
        queryKey: libraryKeys.guest(invitationId),
      })
      // Invalidate all user queries to show transferred images
      queryClient.invalidateQueries({
        queryKey: libraryKeys.user(),
      })
    },
  })
}
